/**
 * Issuer Hub — Sidebar Navigation Configuration
 *
 * Defines the nav items for the Issuer Hub standalone sidebar.
 * Salt icons and the shared Salt shell renderer own navigation expression.
 */

import { SaltIcons } from "@/design-system/salt";
import type { SaltShellIcon } from "@/components/dashboard/salt-shell-navigation";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface IssuerHubNavItem {
  id: string;
  name: string;
  path: string;
  icon: SaltShellIcon;
  /** When true, the nav item is rendered but visually disabled (future feature). */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Nav items
// ---------------------------------------------------------------------------

export const issuerHubNavigation: readonly IssuerHubNavItem[] = [
  {
    id: "pipeline",
    name: "Pipeline",
    path: "/issuer-hub/pipeline",
    icon: SaltIcons.WorkflowIcon,
  },
  {
    id: "campaigns",
    name: "Campaigns",
    path: "/issuer-hub/campaigns",
    icon: SaltIcons.TargetIcon,
  },
  {
    id: "modeling",
    name: "Modeling",
    path: "/issuer-hub/modeling",
    icon: SaltIcons.CalculatorIcon,
  },
  {
    id: "quota",
    name: "Quota",
    path: "/issuer-hub/quota",
    icon: SaltIcons.BarChartIcon,
  },
  {
    id: "prospecting",
    name: "Prospect Pages",
    path: "/issuer-hub/prospecting",
    icon: SaltIcons.DocumentIcon,
  },
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns the active nav item ID based on the current pathname.
 * Falls back to "pipeline" if no match is found.
 */
export function getIssuerHubActiveTab(pathname: string): string {
  if (pathname.startsWith("/issuer-hub/campaigns")) return "campaigns";
  if (pathname.startsWith("/issuer-hub/modeling")) return "modeling";
  if (pathname.startsWith("/issuer-hub/quota")) return "quota";
  if (pathname.startsWith("/issuer-hub/prospecting")) return "prospecting";
  if (pathname.startsWith("/issuer-hub/pipeline")) return "pipeline";
  return "pipeline";
}

/**
 * The Issuer Hub chrome title — which `SaltPageChrome` renders as the page's `<h1>`.
 *
 * Five real routes plus `/issuer-hub` itself, which is a `<Navigate>` to `pipeline` and
 * so borrows its title for the one commit the shell renders before redirecting.
 *
 * Unmapped paths throw in dev and fall back to the section name in production, matching
 * `getPageTitle`'s policy — see the note there for why the two halves differ. Five routes
 * is small enough that a `startsWith` chain is honest here; the main shell's 88 are not,
 * which is why that one is a table.
 *
 * `quota` reads "Quota", not "Quota Dashboard". While the layout and the page each
 * rendered a heading, they disagreed — the chrome said "Quota Dashboard" and
 * `QuotaDashboardView` said "Quota" — so the document made two claims about what it was.
 * Now that one heading survives, it takes the page's wording, which also matches the
 * sidebar nav label.
 */
export function getIssuerHubPageTitle(pathname: string): string {
  if (pathname.startsWith("/issuer-hub/campaigns")) return "Campaigns";
  if (pathname.startsWith("/issuer-hub/modeling")) return "Financial Modeling";
  if (pathname.startsWith("/issuer-hub/quota")) return "Quota";
  if (pathname.startsWith("/issuer-hub/prospecting")) return "Prospect Pages";
  if (pathname.startsWith("/issuer-hub/pipeline")) return "Pipeline";
  if (pathname === "/issuer-hub") return "Pipeline";

  if (import.meta.env.DEV) {
    throw new Error(
      `No Issuer Hub page title is mapped for "${pathname}". The chrome renders this ` +
        "title as the page's <h1>, so an unmapped route ships a page with no heading. " +
        "Add it to getIssuerHubPageTitle in issuer-hub-navigation.ts.",
    );
  }

  return "Issuer Hub";
}
